import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import { useMemo, ReactNode } from 'react';
import { ChildrenData } from 'src/interfaces/Children';
import ChildSelect from 'src/components/common/Dropdown/SelectChild';
import { ParentChildViewSection } from 'src/components/Sidebar/BottomSection/ParentChildView';
import { ChatSession } from 'src/interfaces/Chat';
import { BaseSidebar } from './BaseSidebar';
import {
  setSelectedChild,
  setSelectedSession,
} from 'src/redux/redux-store/children/childrenReducer';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import { parentRoutes } from 'src/navigators/routes/ParentRoutes';
import { useNavigate } from 'react-router-dom';
import { userRoutes } from 'src/navigators/routes/user';

interface SideBarProps {
  selectedSession: string;
  childSessions: ChatSession[];
  onSelectSession: (id: string) => void;
  children: ChildrenData[];
  selectedChild: string;
  onSelectChild: (username: string) => void;
}

const ParentChildViewSidebar: React.FC<Partial<SideBarProps>> = ({
  selectedSession: propSelectedSession,
  childSessions: propChildSessions,
  onSelectSession: propOnSelectSession,
  children: propChildren,
  selectedChild: propSelectedChild,
  onSelectChild: propOnSelectChild,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    children: stateChildren,
    selectedChild: stateSelectedChild,
    childSessions: stateChildSessions,
    selectedSession: stateSelectedSession,
  } = useAppSelector((state) => state.child);

  // Use props if provided, otherwise use state from Redux
  const children = propChildren || stateChildren || [];
  const selectedChild = propSelectedChild || stateSelectedChild || '';
  const childSessions = propChildSessions || stateChildSessions || [];
  const selectedSession = propSelectedSession || stateSelectedSession || '';

  // Use provided callbacks or fall back to dispatching actions
  const onSelectSession =
    propOnSelectSession ||
    ((id: string) => {
      dispatch(setSelectedSession(id));
    });

  const onSelectChild =
    propOnSelectChild ||
    ((username: string) => {
      dispatch(setSelectedChild(username));
    });

  const sidebarContent = useMemo(
    () => (
      <>
        <CustomButton
          onClick={() =>
            selectedChild && navigate(
              `${parentRoutes.CHILD_VIEW_PROGRESS_REPORT}?child=${selectedChild}`
            )
          }
          variant="outline"
          size="default"
          className="text-sm font-semibold mb-4"
          disabled={!selectedChild}
        >
          View Progress Report
        </CustomButton>
        <ChildSelect
          onSelectChild={onSelectChild}
          children={children}
          value={selectedChild}
        />
        <ParentChildViewSection
          selectSession={onSelectSession}
          selectedSession={selectedSession}
          chats={childSessions}
        />
      </>
    ),
    [
      children,
      selectedChild,
      selectedSession,
      childSessions,
      onSelectChild,
      onSelectSession,
    ]
  );

  return <BaseSidebar screenName="Learner View">{sidebarContent}</BaseSidebar>;
};

export default ParentChildViewSidebar;
